import { useEffect } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { ControlledCheckboxAutoComplete } from 'components/shared/ControlledFields';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetClusters, useGetClustersPreferred } from 'queries';
import useAddCluster from './hooks/useAddCluster';
import useRemoveCluster from './hooks/useRemoveCluster';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

const schema = yup.object({
  rosterClusters: yup.array().required().default([])
});

type FormValues = yup.InferType<typeof schema>;

const Clusters = ({ brId }) => {
  const { t } = useTranslation('common');
  const { control, setValue, watch } = useForm<FormValues>({
    defaultValues: schema.cast({
      defaultClusters: []
    }),
    resolver: yupResolver(schema)
  });

  const { data: clusters, isFetching: isFetchingClusters } = useGetClusters();
  const { data: preferredClusters, refetch } = useGetClustersPreferred(brId);

  const addCluster = useAddCluster();
  const removeCluster = useRemoveCluster();
  const clusterWatch = watch('rosterClusters');

  // Set default values
  useEffect(() => {
    if (preferredClusters) {
      const defaultClusters = preferredClusters.map(
        (cluster) => cluster.clusterId
      );
      setValue('rosterClusters', defaultClusters);
    }
  }, [preferredClusters, setValue]);

  const handleMutation = (
    id: string | number | undefined,
    mutation: (payload: any, options: any) => void,
    currentValues: Array<string | number>,
    isAdd: boolean
  ) => {
    const payload = isAdd
      ? { brId, clusterId: id }
      : { brId, brLinkClusterId: id };

    mutation(payload, {
      onSuccess: () => {
        setValue(
          'rosterClusters',
          isAdd ? [...currentValues, id] : currentValues.filter((v) => v !== id)
        );
        refetch();
      }
    });
  };

  // handle change inside the dropdown checkbox
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedClusters = Array.from(
      event as unknown as Set<string | number>
    );
    const currentValues = clusterWatch;

    const addedCluster = selectedClusters.find(
      (cluster) => !currentValues.includes(cluster)
    );
    const removedCluster = currentValues.find(
      (cluster) => !selectedClusters.includes(cluster)
    );
    const removedLinkedCluster = preferredClusters?.find(
      (cluster) => cluster.clusterId === removedCluster
    );
    const brLinkClusterId = removedLinkedCluster?.brLinkClusterId;

    if (addedCluster) {
      handleMutation(addedCluster, addCluster.mutate, currentValues, true);
    }

    if (removedCluster && brLinkClusterId) {
      handleMutation(
        brLinkClusterId,
        removeCluster.mutate,
        currentValues,
        false
      );
    }
  };

  // handle remove in the list below the dropdownlist
  const handleRemove = (idToRemove: number | string) => {
    const currentValues = clusterWatch;
    const removedLinkedCluster = preferredClusters?.find(
      (cluster) => cluster.clusterId === idToRemove
    );
    const brLinkClusterId = removedLinkedCluster?.brLinkClusterId;

    handleMutation(brLinkClusterId, removeCluster.mutate, currentValues, false);
  };

  return (
    <StyledPaper>
      <GridContainer>
        <GridCompact6>
          <Typography variant="h6" marginBottom={2}>
            {t('tableTabs.CLUSTERS')}
          </Typography>
          {isFetchingClusters ? (
            <Skeleton variant="rounded" width="100%" height="40px" />
          ) : (
            <ControlledCheckboxAutoComplete
              fullWidth
              name="rosterClusters"
              label={t('tableTabs.CLUSTERS')}
              variant="outlined"
              options={clusters ?? []}
              control={control}
              idProperty="clusterId"
              labelProperty="name"
              allowItemRemoval
              onChange={(event) => handleChange(event)}
              onRemoveElement={(idToRemove) => handleRemove(idToRemove)}
            />
          )}
        </GridCompact6>
      </GridContainer>
    </StyledPaper>
  );
};

export default Clusters;
